import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export type ResourcesValue = {
  cpuRequest?: string;
  memoryRequest?: string;
  cpuLimit?: string;
  memoryLimit?: string;
};

const FIELDS: Array<{ key: keyof ResourcesValue; label: string; helper: string }> = [
  { key: 'cpuRequest', label: 'CPU Request', helper: 'e.g. 250m, 1' },
  { key: 'memoryRequest', label: 'Memory Request', helper: 'e.g. 256Mi, 1Gi' },
  { key: 'cpuLimit', label: 'CPU Limit', helper: 'e.g. 500m, 2' },
  { key: 'memoryLimit', label: 'Memory Limit', helper: 'e.g. 512Mi, 2Gi' },
];

/**
 * Reusable controlled editor for optional CPU/memory requests and limits.
 * Empty fields are stripped so the rendered template can fall back to defaults.
 */
export const ResourcesEditor = ({
  value,
  onChange,
}: {
  value: ResourcesValue;
  onChange: (next: ResourcesValue) => void;
}) => {
  const update = (key: keyof ResourcesValue, raw: string) => {
    const next: ResourcesValue = { ...value };
    if (raw === '') {
      delete next[key];
    } else {
      next[key] = raw;
    }
    onChange(next);
  };

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {FIELDS.map(f => (
        <TextField
          key={f.key}
          label={f.label}
          value={value[f.key] ?? ''}
          onChange={e => update(f.key, e.target.value)}
          margin="dense"
          variant="outlined"
          style={{ flex: '1 1 180px' }}
          helperText={f.helper}
        />
      ))}
    </div>
  );
};

/**
 * Scaffolder field wrapper around ResourcesEditor.
 * Usage: `ui:field: Resources` on a field of type `object`.
 */
export const ResourcesField = ({
  formData,
  onChange,
  schema,
}: FieldExtensionComponentProps<ResourcesValue>) => (
  <FormControl component="fieldset" margin="normal" fullWidth>
    {schema.title && <FormLabel component="legend">{schema.title}</FormLabel>}
    {schema.description && (
      <FormHelperText style={{ marginBottom: 8 }}>{schema.description}</FormHelperText>
    )}
    <ResourcesEditor value={formData ?? {}} onChange={onChange} />
  </FormControl>
);
